import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { GlassPanel } from "../components/GlassPanel";
import ChatBox from "../components/ChatBox";
import MultiChannelChat from "../components/MultiChannelChat";
import OverloadAlerts from "../components/OverloadAlerts";
import SchedulePage from "./SchedulePage";
import { useAuth } from "../context/AuthContext";
import GuidedTourModal from "../components/GuidedTourModal";
import {
  Wrench, FileText, Receipt, Package, ArrowRight, CalendarClock, AlertTriangle, Sparkles, TrendingUp, Activity,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";

const StatTile = ({ label, value, icon: Icon, accent, testId }) => (
  <GlassPanel data-testid={testId} className="!p-5">
    <div className="flex items-center justify-between mb-3">
      <div className="text-label">{label}</div>
      <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${accent}`}>
        <Icon className="w-4 h-4" />
      </div>
    </div>
    <div className="font-heading text-3xl font-light tracking-tight text-white">{value}</div>
  </GlassPanel>
);

export default function DashboardPage() {
  const { user } = useAuth();
  const location = useLocation();
  const openChannel = location.state?.openChannel;
  const [summary, setSummary] = useState(null);
  const [priorities, setPriorities] = useState(null);
  const isAdmin = user?.role === "admin" || user?.role === "office";

  useEffect(() => {
    (async () => {
      const [s, p] = await Promise.all([
        api.get("/analytics/summary"),
        api.get("/dashboard/priorities"),
      ]);
      setSummary(s.data);
      setPriorities(p.data);
    })();
  }, []);

  return (
    <div className="space-y-6">
      <GuidedTourModal />
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-end justify-between flex-wrap gap-4"
      >
        <div>
          <div className="text-label">{isAdmin ? "Operations" : "My Workspace"}</div>
          <h1 className="font-heading text-4xl md:text-5xl font-light tracking-tighter text-white mt-1">
            Welcome, {user?.name?.split(" ")[0] || "Operator"}.
          </h1>
          <p className="text-slate-400 mt-2 text-sm md:text-base max-w-xl">
            {isAdmin
              ? "Here's what's on the shop floor today — jobs, quotes and signals."
              : "Track your assigned wraps and progress through the day."}
          </p>
        </div>
        <div className="pill pill-info">
          <Sparkles className="w-3 h-3" />
          {user?.is_impersonating ? "Read-only view" : user?.role === "admin" ? "Master Console" : user?.role === "office" ? "Office Console" : "Installer Mini"}
        </div>
      </motion.div>

      {/* Overload alert — surfaces installers ≥ 90% next week so admins can
          redistribute before Monday. Component self-hides when calm. */}
      {isAdmin && <OverloadAlerts />}

      {/* KPI grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {isAdmin ? (
          <>
            <StatTile testId="stat-leads" label="Open Leads" value={summary?.leads_count ?? "—"} icon={TrendingUp} accent="bg-blue-500/10 text-blue-300 border border-blue-400/20" />
            <StatTile testId="stat-quotes" label="Quotes" value={summary?.quotes_count ?? "—"} icon={FileText} accent="bg-amber-500/10 text-amber-300 border border-amber-400/20" />
            <StatTile testId="stat-invoices" label="Invoices" value={summary?.invoices_count ?? "—"} icon={Receipt} accent="bg-emerald-500/10 text-emerald-300 border border-emerald-400/20" />
            <StatTile testId="stat-jobs" label="Jobs" value={summary?.total_jobs ?? "—"} icon={Wrench} accent="bg-violet-500/10 text-violet-300 border border-violet-400/20" />
          </>
        ) : (
          <>
            <StatTile testId="stat-my-jobs" label="My Jobs" value={summary?.total_jobs ?? "—"} icon={Wrench} accent="bg-blue-500/10 text-blue-300 border border-blue-400/20" />
            <StatTile testId="stat-scheduled" label="Scheduled" value={summary?.scheduled_jobs ?? "—"} icon={CalendarClock} accent="bg-amber-500/10 text-amber-300 border border-amber-400/20" />
            <StatTile testId="stat-in-progress" label="In Progress" value={summary?.in_progress_jobs ?? "—"} icon={Activity} accent="bg-violet-500/10 text-violet-300 border border-violet-400/20" />
            <StatTile testId="stat-completed" label="Completed" value={summary?.completed_jobs ?? "—"} icon={TrendingUp} accent="bg-emerald-500/10 text-emerald-300 border border-emerald-400/20" />
          </>
        )}
      </div>

      {/* Priorities + Chat */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Priorities - 2 cols */}
        <GlassPanel className="lg:col-span-2 !p-6" delay={0.05}>
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="text-label">Current Priorities</div>
              <h2 className="font-heading text-2xl text-white mt-1">What needs attention</h2>
            </div>
            <AlertTriangle className="w-5 h-5 text-amber-300" />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {/* Upcoming jobs */}
            <div data-testid="priority-upcoming-jobs" className="bg-white/[0.02] border border-white/10 rounded-2xl p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs uppercase tracking-widest text-slate-400 flex items-center gap-2">
                  <CalendarClock className="w-3.5 h-3.5" /> Upcoming Jobs
                </div>
                <Link to="/jobs" className="text-xs text-blue-300 hover:text-blue-200 flex items-center gap-1">
                  View <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
              {(!priorities?.upcoming_jobs || priorities.upcoming_jobs.length === 0) ? (
                <div className="text-xs text-slate-500 italic py-3">No upcoming jobs.</div>
              ) : (
                <ul className="space-y-2">
                  {priorities.upcoming_jobs.slice(0, 4).map((j) => (
                    <li key={j.id} className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm text-white truncate">{j.vehicle}</div>
                        <div className="text-xs text-slate-500 truncate">{j.contact_name} · {j.service}</div>
                      </div>
                      <span className={`pill ${["wrapping", "assembly", "disassembly", "wash_down"].includes(j.sub_status) ? "pill-warning" : "pill-info"}`}>
                        {(j.sub_status || j.status || "booked").replace(/_/g, " ")}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Quote requests (admin only) */}
            {isAdmin && (
              <div data-testid="priority-quote-requests" className="bg-white/[0.02] border border-white/10 rounded-2xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs uppercase tracking-widest text-slate-400 flex items-center gap-2">
                    <FileText className="w-3.5 h-3.5" /> Quote Requests
                  </div>
                  <Link to="/quotes" className="text-xs text-blue-300 hover:text-blue-200 flex items-center gap-1">
                    View <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
                {(!priorities?.quote_requests || priorities.quote_requests.length === 0) ? (
                  <div className="text-xs text-slate-500 italic py-3">No pending quotes.</div>
                ) : (
                  <ul className="space-y-2">
                    {priorities.quote_requests.slice(0, 4).map((q) => (
                      <li key={q.id} className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-sm text-white truncate">{q.contact_name}</div>
                          <div className="text-xs text-slate-500 truncate">{q.vehicle} · ${Number(q.amount).toLocaleString()}</div>
                        </div>
                        <span className="pill pill-warning">{q.sub_status || q.status}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* Materials */}
            <div data-testid="priority-materials" className="bg-white/[0.02] border border-white/10 rounded-2xl p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs uppercase tracking-widest text-slate-400 flex items-center gap-2">
                  <Package className="w-3.5 h-3.5" /> Materials Needed
                </div>
              </div>
              {(!priorities?.materials_needed || priorities.materials_needed.length === 0) ? (
                <div className="text-xs text-slate-500 italic py-3">No material requirements queued.</div>
              ) : (
                <ul className="space-y-2">
                  {priorities.materials_needed.slice(0, 5).map((m) => (
                    <li key={m.name} className="flex items-center justify-between">
                      <span className="text-sm text-white">{m.name}</span>
                      <span className="text-xs text-slate-400">{m.jobs_needing} job{m.jobs_needing > 1 ? "s" : ""}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Open invoices (admin) */}
            {isAdmin && (
              <div data-testid="priority-open-invoices" className="bg-white/[0.02] border border-white/10 rounded-2xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs uppercase tracking-widest text-slate-400 flex items-center gap-2">
                    <Receipt className="w-3.5 h-3.5" /> Open Invoices
                  </div>
                  <Link to="/invoices" className="text-xs text-blue-300 hover:text-blue-200 flex items-center gap-1">
                    View <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
                {(!priorities?.open_invoices || priorities.open_invoices.length === 0) ? (
                  <div className="text-xs text-slate-500 italic py-3">All clear — no open invoices.</div>
                ) : (
                  <ul className="space-y-2">
                    {priorities.open_invoices.slice(0, 4).map((iv) => (
                      <li key={iv.id} className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-sm text-white truncate">{iv.contact_name}</div>
                          <div className="text-xs text-slate-500">${Number(iv.amount).toLocaleString()}</div>
                        </div>
                        <span className={`pill ${iv.status === "overdue" ? "pill-error" : "pill-warning"}`}>{iv.status}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </GlassPanel>

        {/* Chat */}
        <GlassPanel className="!p-5" delay={0.1}>
          <MultiChannelChat compact defaultChannelId={openChannel || "team"} />
        </GlassPanel>
      </div>

      {/* Schedule — moved out of its own tab and dropped below priorities.
          Admins get an installer picker so they can peek at any crew
          member's day; installers see their own by default. */}
      <GlassPanel data-testid="dashboard-schedule" className="!p-6" delay={0.15}>
        <SchedulePage embedded />
      </GlassPanel>
    </div>
  );
}
